import React from "react";
import { motion } from "framer-motion";
import { BOOK_COVERS, BOOK_FORMATS, AMAZON_AUTHOR_URL } from "../lib/content";
import { withAffiliate } from "../lib/affiliate";
import { ExternalLink, ShoppingBag, BookOpen, ArrowRight } from "lucide-react";

const FORMAT_META = {
  Paperback: {
    icon: "📘",
    note: "Print edition",
  },
  Hardcover: {
    icon: "📕",
    note: "Collector's edition",
  },
  Kindle: {
    icon: "⚡",
    note: "Instant download",
  },
};

const BuySection = ({ books }) => {
  return (
    <section
      id="buy"
      data-testid="buy-section"
      className="relative py-28 md:py-40"
      style={{ background: "linear-gradient(180deg,#0D2214 0%,#08140B 100%)" }}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <span className="font-accent text-xl text-amber-300">Bring the Magic Home</span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mt-3">
            Available on <span className="italic text-gradient-gold">Amazon</span>
          </h2>
          <div className="section-divider mt-6" />
          <p className="mt-6 text-emerald-50/75 max-w-2xl mx-auto text-lg">
            Every book in <em className="not-italic text-amber-200">The Carmel Prince of Miara Mountain</em> series is available worldwide on
            Amazon — in paperback, hardcover, and Kindle.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
          {(books || []).map((book, idx) => {
            const formats = BOOK_FORMATS[book.id] || [];
            return (
              <motion.div
                key={book.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: idx * 0.1 }}
                className="glass-card p-6 md:p-8 flex flex-col"
                data-testid={`buy-card-${book.id}`}
              >
                <div className="flex gap-5">
                  <img
                    src={BOOK_COVERS[book.id]}
                    alt={book.title}
                    className="w-28 h-40 object-cover rounded-lg shadow-xl shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-accent text-amber-300 text-sm">
                      {book.subtitle?.split("—")[0]?.trim()}
                    </div>
                    <h3 className="font-heading text-2xl text-white leading-tight mt-1">
                      {book.title}
                    </h3>
                    <div className="text-amber-200/80 text-sm mt-1">
                      {book.subtitle?.split("—")[1]?.trim()}
                    </div>
                    <p className="text-emerald-50/70 text-sm mt-3 leading-relaxed line-clamp-3">
                      {book.synopsis}
                    </p>
                  </div>
                </div>

                <div className="mt-7">
                  <div className="text-xs uppercase tracking-[0.25em] text-emerald-50/55 mb-3">
                    Choose your format
                  </div>
                  <div className="space-y-2.5">
                    {formats.map((f) => {
                      const meta = FORMAT_META[f.label] || {};
                      return (
                        <a
                          key={f.label}
                          href={withAffiliate(f.url)}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid={`buy-${book.id}-${f.label.toLowerCase()}`}
                          className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl border border-amber-500/20 hover:border-amber-400/60 hover:bg-amber-400/5 transition-all group"
                        >
                          <div className="flex items-center gap-3">
                            <span className="text-2xl" aria-hidden>{meta.icon}</span>
                            <div>
                              <div className="font-medium text-white text-base">{f.label}</div>
                              <div className="text-xs text-emerald-50/55">{meta.note}</div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className="font-heading text-xl text-gradient-gold">
                              ${Number(f.price).toFixed(2)}
                            </span>
                            <ArrowRight size={16} className="text-amber-300 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <a
            href={withAffiliate(AMAZON_AUTHOR_URL)}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-gold"
            data-testid="buy-amazon-store"
          >
            <ExternalLink size={16} />
            All Books on Amazon
          </a>
          <p className="text-xs text-emerald-50/55 mt-5 max-w-md mx-auto">
            Includes Kindle Unlimited eligibility on select titles · Free
            shipping on $35+ orders
          </p>
        </div>
      </div>
    </section>
  );
};

export default BuySection;
